<template>
  <div class="ntv-butterfly-chart flex">
    <ntv-bar-chart
      ref="barChartLeft"
      class="ntv-butterfly-chart__left flex-1"
      is-inverted
      horizontal
      show-point-labels
      bars-clickable
      :bar-width="barWidth"
      :labels="formatted.labels"
      :series="formatted.seriesLeft"
      :animation-duration="animationDuration"
      :animation-begin="animationBegin"
      :total="totals.left"
      :show-percentages="showPercentages"
      :show-tooltip="showTotals.left"
      @barClick="onBarClick"
      :options="{
        ...cOptions.left,
        axisX: {
          offset: 0,
          showLabel: false,
          showGrid: false,
          ...cOptions.left.axisX
        },
        axisY: {
          offset: 0,
          showLabel: false,
          showGrid: false,
          ...cOptions.left.axisY
        },
        pointLabels: {
          textAnchor: 'end',
          ...cOptions.left.pointLabels
        }
      }"
    />
    <div
      class="ntv-butterfly-chart__labels-y px-5xl flex flex-col border-l border-r border-light"
      :style="{
        width: labelsWidth
      }"
    >
      <ntv-center-label
        class="ntv-butterfly-chart__label flex flex-1 justify-center items-center leading-tight"
        :text="label"
        size="md"
        v-for="(label, index) in formatted.labels"
        :key="label"
        @click="onClick(data[index])"
        @mouseenter="onMouseEnter(index)"
        @mouseleave="onMouseLeave(index)"
      />
    </div>
    <ntv-bar-chart
      ref="barChartRight"
      class="ntv-butterfly-chart__right flex-1"
      horizontal
      show-point-labels
      bars-clickable
      :bar-width="barWidth"
      :labels="formatted.labels"
      :series="formatted.seriesRight"
      :animation-duration="animationDuration"
      :animation-begin="animationBegin"
      :total="totals.right"
      :show-percentages="showPercentages"
      :show-tooltip="showTotals.right"
      @barClick="onBarClick"
      :options="{
        ...cOptions.right,
        axisX: {
          offset: 0,
          showLabel: false,
          showGrid: false,
          ...cOptions.right.axisX
        },
        axisY: {
          offset: 0,
          showLabel: false,
          showGrid: false,
          ...cOptions.right.axisY
        },
        pointLabels: {
          textAnchor: 'start',
          ...cOptions.right.pointLabels
        }
      }"
    />
  </div>
</template>

<script>
import NtvBarChart from './BarChart'
import NtvCenterLabel from './CenterLabel'

export default {
  name: 'NtvButterflyChart',

  components: {
    NtvBarChart,
    NtvCenterLabel
  },

  props: {
    /**
     * Chart data will be an array of objects with each object representing a
     * horizontal row. If an array is passed to the values (valueLeft,
     * valueRight), then the length must be the same across all items or there
     * will be misalignments.
     *
     * ```
     * [
     *  ...
     *  {
     *    label: 'Arts',
     *    valueLeft: [850],
     *    valueRight: [2.3],
     *  }
     *  ...
     * ]
     * ```
     */
    data: {
      type: Array,
      default: () => []
    },
    /**
     * Totals for left and right columns. `left` and/or `right` must be specified in order for the percentages to be
     * displayed. In order to only display percentages for a single side, only specify a value for that side.
     */
    totals: {
      type: Object,
      default: () => ({
        left: null,
        right: null
      })
    },
    /**
     * Whether or not to show percentages in the point labels for each column. At least one value needs to be
     * provided to the `totals` prop in order for this to work.
     */
    showPercentages: {
      type: Boolean,
      default: false
    },
    /**
     * Whether or not to display a totals tooltip on bar hover.
     */
    showTotals: {
      type: Object,
      default: () => ({
        left: true,
        right: true
      })
    },
    /**
     * Width of an individual bar
     */
    barWidth: {
      type: Number,
      default: 20
    },
    /**
     * The width of the labels container in the middle.
     */
    labelsWidth: {
      type: String,
      default: null
    },
    /**
     * Options for both the left and right side of the butterfly chart. View
     * available options per side here:
     * https://gionkunz.github.io/chartist-js/api-documentation.html#chartistbar-declaration-defaultoptions
     */
    options: {
      type: Object,
      default: () => ({
        left: {},
        right: {}
      })
    },
    /**
     * How long the whole chart animation takes.
     */
    animationDuration: {
      type: String,
      default: '0.3s'
    },
    /**
     * The delay before each bar animation.
     */
    animationBegin: {
      type: Number,
      default: 35
    }
  },

  data () {
    return {
      currIndex: 0,
      leftBars: [],
      rightBars: []
    }
  },

  computed: {
    cOptions () {
      return {
        left: {},
        right: {},
        ...this.options
      }
    },
    formatted () {
      const reduced = this.data
        .reduce((acc, item, index) => {
          acc.labels.push(item.label)
          // Since we require both left and right values, iterate a static list
          // to handle both sides.
          const positions = ['Left', 'Right']
          positions.forEach(pos => {
            // single item will contain label, valueLeft, and valueRight
            if (`value${pos}` in item) {
              if (typeof item[`value${pos}`] === 'number') {
                if (acc[`series${pos}`][0]) {
                  acc[`series${pos}`][0].push(item[`value${pos}`])
                } else {
                  // add value in an array for initial setup.
                  acc[`series${pos}`][0] = [item[`value${pos}`]]
                }
              } else if (Array.isArray(item[`value${pos}`])) {
                // If value is an array, iterate each value to place in its own
                // array since there can be multiple series.
                item[`value${pos}`].forEach((val, index) => {
                  if (acc[`series${pos}`][index]) {
                    acc[`series${pos}`][index].push(val)
                  } else {
                    // add value in an array for initial setup.
                    acc[`series${pos}`][index] = [val]
                  }
                })
              }
            } else {
              console.error(
                `value${pos} key is missing in object at index: ` + index
              )
            }
          })

          return acc
        },
        {
          labels: [],
          seriesLeft: [[]],
          seriesRight: [[]]
        }
      )

      return reduced
    }
  },

  methods: {
    // Provides the clicked label row data
    onClick (currItem) {
      this.$emit('labelClick', currItem)
    },
    onBarClick (barIdx) {
      this.onClick(this.data[barIdx])
    },
    // We need to programmatically set bar chart color when label is hovered
    // since they are separate elements
    // The current item index so we can grab the svg bar related to the label
    onMouseEnter (index) {
      const barChartLeft = this.$refs.barChartLeft.$el
      const barChartRight = this.$refs.barChartRight.$el

      const allLeftBars = barChartLeft.querySelectorAll('.ct-bar')
      const allRightBars = barChartRight.querySelectorAll('.ct-bar')

      // Because charist reverse the bars, we need to get reverse of the index
      const actualIndex = allLeftBars.length - 1 - index

      // Store reference so we don't have to query on mouseleave
      this.currIndex = actualIndex
      this.leftBars = allLeftBars
      this.rightBars = allRightBars

      this.leftBars[this.currIndex].classList.add('ct-bar__hovered')
      this.rightBars[this.currIndex].classList.add('ct-bar__hovered')
    },
    onMouseLeave () {
      this.leftBars[this.currIndex].classList.remove('ct-bar__hovered')
      this.rightBars[this.currIndex].classList.remove('ct-bar__hovered')
    }
  }
}
</script>

<style lang="scss">
.ntv-butterfly-chart__left {
  .ct-series-a .ct-bar {
    stroke: #9ddbec;

    &:hover,
    &.ct-bar__hovered {
      stroke: #50b1cb;
    }
  }
}
.ntv-butterfly-chart__right {
  .ct-series-a .ct-bar {
    stroke: #a3e5ad;

    &:hover,
    &.ct-bar__hovered {
      stroke: #67bd74;
    }
  }
}

.ntv-butterfly-chart__labels-y {
  padding-bottom: 3px;
}
</style>
